/**
 * Device Info Modal - Shows franchise login device details
 */

import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Smartphone, Clock, Wifi, WifiOff, MapPin, RefreshCw, Monitor, Globe, Cpu, Signal } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const DeviceInfoModal = ({ isOpen, onClose, franchise }) => {
  const [deviceInfo, setDeviceInfo] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && franchise) {
      fetchDeviceInfo();
    }
  }, [isOpen, franchise]);

  const fetchDeviceInfo = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/franchises/${franchise.id}/active-device`);
      setDeviceInfo(response.data);
    } catch (error) {
      console.error('Error fetching device info:', error);
      toast.error('Failed to load device information');
    } finally {
      setLoading(false);
    }
  };

  const getTimeDifference = (timestamp) => {
    if (!timestamp) return 'Never';
    
    const now = new Date();
    const then = new Date(timestamp);
    const diffMs = now - then;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  };

  const isOnline = (lastActive) => {
    if (!lastActive) return false;
    
    const now = new Date();
    const then = new Date(lastActive);
    const diffMins = Math.floor((now - then) / 60000);
    
    // Consider online if last active within 5 minutes
    return diffMins < 5;
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    const date = new Date(timestamp);
    return date.toLocaleString('en-IN', {
      dateStyle: 'medium',
      timeStyle: 'short'
    });
  };

  if (loading) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Device Information</DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="h-8 w-8 animate-spin text-accent" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const online = deviceInfo?.has_active_device && isOnline(deviceInfo?.last_active);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Smartphone className="h-5 w-5" />
            Device Information
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Franchise Name */}
          <div className="bg-gray-50 p-3 rounded-lg">
            <h3 className="font-semibold text-lg">{franchise?.name}</h3>
            <p className="text-sm text-gray-600">{franchise?.id}</p>
          </div>

          {deviceInfo?.has_active_device ? (
            <>
              {/* Online/Offline Status */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2">
                  {online ? (
                    <>
                      <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                      <Wifi className="h-4 w-4 text-green-600" />
                      <span className="font-medium text-green-700">Online</span>
                    </>
                  ) : (
                    <>
                      <div className="w-3 h-3 bg-gray-400 rounded-full"></div>
                      <WifiOff className="h-4 w-4 text-gray-600" />
                      <span className="font-medium text-gray-700">Offline</span>
                    </>
                  )}
                </div>
                <span className="text-sm text-gray-600">
                  {getTimeDifference(deviceInfo.last_active)}
                </span>
              </div>

              {/* Device Details */}
              <div className="space-y-3">
                {/* Device Name & Type */}
                <div className="flex items-start gap-3 p-3 border rounded-lg">
                  <Smartphone className="h-5 w-5 text-accent mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-600">Device</p>
                    <p className="font-semibold">{deviceInfo.device_name || 'Unknown Device'}</p>
                    {deviceInfo.device_info?.deviceType && (
                      <p className="text-xs text-gray-500 mt-0.5">{deviceInfo.device_info.deviceType}</p>
                    )}
                  </div>
                </div>

                {/* Browser & OS */}
                {deviceInfo.device_info?.browser && (
                  <div className="flex items-start gap-3 p-3 border rounded-lg">
                    <Monitor className="h-5 w-5 text-accent mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-600">Browser & OS</p>
                      <p className="font-semibold">
                        {deviceInfo.device_info.browser} {deviceInfo.device_info.browserVersion}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {deviceInfo.device_info.os}
                      </p>
                    </div>
                  </div>
                )}

                {/* Screen & Display */}
                {deviceInfo.device_info?.screen && (
                  <div className="flex items-start gap-3 p-3 border rounded-lg">
                    <Cpu className="h-5 w-5 text-accent mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-600">Display</p>
                      <p className="font-semibold">
                        {deviceInfo.device_info.screen.width} × {deviceInfo.device_info.screen.height}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Pixel Ratio: {deviceInfo.device_info.screen.pixelRatio}x
                      </p>
                    </div>
                  </div>
                )}

                {/* Location (Timezone) */}
                {deviceInfo.device_info?.timezone && (
                  <div className="flex items-start gap-3 p-3 border rounded-lg">
                    <Globe className="h-5 w-5 text-accent mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-600">Location</p>
                      <p className="font-semibold">{deviceInfo.device_info.timezone}</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Locale: {deviceInfo.device_info.locale}
                      </p>
                    </div>
                  </div>
                )}

                {/* Connection Status */}
                {deviceInfo.device_info?.connection?.effectiveType && deviceInfo.device_info.connection.effectiveType !== 'Unknown' && (
                  <div className="flex items-start gap-3 p-3 border rounded-lg">
                    <Signal className="h-5 w-5 text-accent mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-600">Connection</p>
                      <p className="font-semibold capitalize">{deviceInfo.device_info.connection.effectiveType}</p>
                    </div>
                  </div>
                )}

                {/* Login Time */}
                <div className="flex items-start gap-3 p-3 border rounded-lg">
                  <Clock className="h-5 w-5 text-accent mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-600">Login Time</p>
                    <p className="font-semibold">{formatDateTime(deviceInfo.login_time)}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {getTimeDifference(deviceInfo.login_time)}
                    </p>
                  </div>
                </div>

                {/* Last Active */}
                <div className="flex items-start gap-3 p-3 border rounded-lg">
                  <Clock className="h-5 w-5 text-accent mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-600">Last Active</p>
                    <p className="font-semibold">{formatDateTime(deviceInfo.last_active)}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {getTimeDifference(deviceInfo.last_active)}
                    </p>
                  </div>
                </div>

                {/* Device ID (for debugging) */}
                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <p className="text-xs font-medium text-gray-500">Device ID</p>
                    <p className="text-xs font-mono text-gray-700 break-all">
                      {deviceInfo.device_id}
                    </p>
                  </div>
                </div>
              </div>

              {/* Refresh Button */}
              <div className="flex justify-center pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchDeviceInfo}
                  className="gap-2"
                >
                  <RefreshCw className="h-4 w-4" />
                  Refresh Status
                </Button>
              </div>
            </>
          ) : (
            <div className="text-center py-8">
              <WifiOff className="h-12 w-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600 font-medium">No Active Session</p>
              <p className="text-sm text-gray-500 mt-2">
                This franchise is not currently logged in on any device
              </p>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DeviceInfoModal;
